import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  applyDocumentLocale,
  applyThemeAtBoot,
  registerMockApiModule,
} from '@agnivo/app-core';
import { App } from './App';
import { freezeBootLocale } from './bootLocale';
import { bootstrapTelegram, telegramSystemSchemeDetector } from './telegram';
import { guardAgainstStaleBundle } from './updateGuard';
import './styles/index.css';

// Apply the persisted theme (data-theme) BEFORE first paint so there is no
// light→dark flash. 'system' resolves to Telegram's colorScheme.
applyThemeAtBoot(telegramSystemSchemeDetector);

// Declare the language we are ACTUALLY rendering, before React mounts (product decision
// 2026-08-30, `decisions/product/2026-08-30-document-lang-mirrors-ui-locale.md`), and FREEZE
// that answer so every launch string resolves from the same one. See `bootLocale.ts` for why
// freezing — rather than re-applying after the Telegram handshake — is the coherent contract
// here, and for the separate phone-language race it deliberately leaves open.
//
// ⛔ Do not replace this with a bare `applyDocumentLocale()`. The value must reach
// `getBootLocale()`, or `LaunchGate` goes back to resolving a MUTABLE `detectLocale()` per
// render while this line writes the attribute exactly once — which is the divergence product
// found at `0c4ce89`, not a hypothetical one.
freezeBootLocale(applyDocumentLocale());

// Signal the Telegram WebView ready + expand + wire theme-param sync (no-op when
// running standalone via `pnpm dev`).
//
// Fire-and-forget, and deliberately NOT awaited: the SDK tag is `async`, so this
// now waits for a possibly-late window.Telegram.WebApp. Awaiting it here would
// put a third-party CDN's latency back in front of first paint — reintroducing,
// in JS, exactly the boot dependency that removing the parser-blocking script
// was meant to eliminate. The handshake lands when the SDK does; render does not
// wait for it.
void bootstrapTelegram();

const container = document.getElementById('root');
if (!container) throw new Error('missing #root');

// T1 mock fold (web!515 unblock; Initiative 017): the mock api module is imported
// dynamically HERE, in shell source, where Vite replaces the env read — a live build
// folds the branch dead and ships no mock module at all (the size gate measures every
// chunk, so a lazy mock would still cost its bytes). Mock builds (dev) register before
// React mounts. One microtask of live-build delay; `bootstrapTelegram()` above keeps
// its own fire-and-forget contract untouched.
//
// `?? 'mock'` here MUST match src/config.ts's `(import.meta.env.VITE_API_MODE ?? 'mock')` —
// if they disagree, a build with the env var unset gets `config.apiMode === 'mock'` while
// the shell skips the registration, and ApiProvider's mock arm throws "never registered"
// on first render (the desktop e2e regression class — see `web!520` pipeline 2808614277,
// 2026-09-01).
const mockApiBoot =
  (import.meta.env.VITE_API_MODE ?? 'mock') === 'mock'
    ? import('@agnivo/api-client/mock').then(registerMockApiModule)
    : Promise.resolve();

void mockApiBoot.then(() => {
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});

// Fire-and-forget AFTER first paint: if the Telegram WebView served a stale
// cached bundle, self-heal onto the currently-deployed one (see updateGuard.ts).
// Post-render so an up-to-date launch never blocks LCP.
void guardAgainstStaleBundle();
